import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import FillableFormEditor from "@/components/FillableFormEditor";
import IdentityDocsConfig from "@/components/settings/IdentityDocsConfig";
import InservicesSection from "@/components/InservicesSection";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Video,
  FileText,
  ListChecks,
  Plus,
  Trash2,
  Upload,
  Loader2,
  Save,
  Link as LinkIcon,
  GripVertical,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyKit = { videos: [], inservices: [], documents: [], checklist: [], identity_documents: [] };
const fromServer = (d) => ({
  videos: d.videos || [],
  inservices: d.inservices || [],
  documents: d.documents || [],
  checklist: d.checklist || [],
  identity_documents: d.identity_documents || [],
});

export default function OrientationKitTab({ isAdmin }) {
  const [kit, setKit] = useState(emptyKit);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [fieldEditorIdx, setFieldEditorIdx] = useState(null);

  useEffect(() => {
    fetchKit();
  }, []);

  const fetchKit = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/orientation-kit`);
      setKit(fromServer(res.data));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load orientation kit");
    } finally {
      setLoading(false);
    }
  };

  const saveKit = async (nextKit = kit) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/orientation-kit`, nextKit);
      setKit(fromServer(res.data));
      toast.success("Orientation kit saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save kit");
    } finally {
      setSaving(false);
    }
  };

  // ---- Videos ----
  const addUrlVideo = () => {
    setKit((k) => ({
      ...k,
      videos: [
        ...k.videos,
        {
          title: "New video",
          description: "",
          source_type: "url",
          url: "",
          required: true,
          order: k.videos.length,
        },
      ],
    }));
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVideo(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(
        `${API}/settings/orientation-kit/upload-video`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setKit((k) => ({
        ...k,
        videos: [
          ...k.videos,
          {
            title: file.name.replace(/\.[^.]+$/, ""),
            description: "",
            source_type: "upload",
            storage_path: res.data.storage_path,
            content_type: res.data.content_type,
            required: true,
            order: k.videos.length,
          },
        ],
      }));
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Video upload failed");
    } finally {
      setUploadingVideo(false);
      e.target.value = "";
    }
  };

  const updateVideo = (idx, patch) => {
    setKit((k) => {
      const videos = [...k.videos];
      videos[idx] = { ...videos[idx], ...patch };
      return { ...k, videos };
    });
  };
  const removeVideo = (idx) => {
    setKit((k) => ({ ...k, videos: k.videos.filter((_, i) => i !== idx) }));
  };

  // ---- Documents ----
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(
        `${API}/settings/orientation-kit/upload-document`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setKit((k) => ({
        ...k,
        documents: [
          ...k.documents,
          {
            title: file.name.replace(/\.[^.]+$/, ""),
            description: "",
            storage_path: res.data.storage_path,
            content_type: res.data.content_type,
            signature_style: "esign",
            required: true,
            order: k.documents.length,
            fillable_form: res.data.fillable_form || null,
          },
        ],
      }));
      const n = res.data.detected_field_count || 0;
      if (n > 0) {
        toast.success(`Document uploaded — detected ${n} fillable field${n === 1 ? "" : "s"}. New hires will be able to type into this form.`);
      } else {
        toast.success("Document uploaded. No built-in form fields found — use “Make fillable” to draw fields if new hires should type into it.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Document upload failed");
    } finally {
      setUploadingDoc(false);
      e.target.value = "";
    }
  };

  const updateDoc = (idx, patch) => {
    setKit((k) => {
      const documents = [...k.documents];
      documents[idx] = { ...documents[idx], ...patch };
      return { ...k, documents };
    });
  };
  const removeDoc = (idx) => {
    setKit((k) => ({ ...k, documents: k.documents.filter((_, i) => i !== idx) }));
  };

  // ---- Checklist ----
  const addChecklistItem = () => {
    setKit((k) => ({
      ...k,
      checklist: [
        ...k.checklist,
        { title: "New task", description: "", required: true, order: k.checklist.length },
      ],
    }));
  };
  const updateChecklistItem = (idx, patch) => {
    setKit((k) => {
      const checklist = [...k.checklist];
      checklist[idx] = { ...checklist[idx], ...patch };
      return { ...k, checklist };
    });
  };
  const removeChecklistItem = (idx) => {
    setKit((k) => ({ ...k, checklist: k.checklist.filter((_, i) => i !== idx) }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const readonly = !isAdmin;

  return (
    <div className="space-y-6" data-testid="orientation-kit-tab">
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">New Hire Orientation Kit</h2>
            <p className="text-sm text-slate-600 mt-1 max-w-xl">
              Configure the videos, documents, and checklist items every new hire must complete.
              Send the tokenized link from any employee profile.
            </p>
          </div>
          {!readonly && (
            <Button
              onClick={() => saveKit()}
              disabled={saving}
              className="gap-2"
              data-testid="orientation-kit-save"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
            </Button>
          )}
        </div>
      </div>

      <IdentityDocsConfig
        items={kit.identity_documents}
        readonly={readonly}
        onChange={(identity_documents) => setKit((k) => ({ ...k, identity_documents }))}
      />

      <InservicesSection kit={kit} setKit={setKit} readonly={readonly} />

      {/* Videos */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-slate-900">Videos</h3>
            <span className="text-xs text-slate-500">({kit.videos.length})</span>
          </div>
          {!readonly && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={addUrlVideo} className="gap-1" data-testid="add-video-url-btn">
                <LinkIcon className="w-3 h-3" /> Add URL
              </Button>
              <label className="inline-flex">
                <input
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={handleVideoUpload}
                  disabled={uploadingVideo}
                  data-testid="upload-video-input"
                />
                <Button variant="outline" size="sm" asChild className="gap-1 cursor-pointer">
                  <span>
                    {uploadingVideo ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Upload className="w-3 h-3" />
                    )}
                    Upload MP4
                  </span>
                </Button>
              </label>
            </div>
          )}
        </div>

        {kit.videos.length === 0 ? (
          <p className="text-sm text-slate-500 italic text-center py-6">No videos yet.</p>
        ) : (
          <div className="space-y-3">
            {kit.videos.map((v, idx) => (
              <div
                key={v.id || idx}
                className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50"
              >
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <GripVertical className="w-3 h-3" />
                  <span className="uppercase tracking-wide font-semibold">
                    {v.source_type === "upload" ? "Uploaded" : "External URL"}
                  </span>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <Label className="text-xs">Title</Label>
                    <Input
                      value={v.title || ""}
                      onChange={(e) => updateVideo(idx, { title: e.target.value })}
                      disabled={readonly}
                    />
                  </div>
                  {v.source_type === "url" && (
                    <div>
                      <Label className="text-xs">Video URL (YouTube/Vimeo embed or direct MP4)</Label>
                      <Input
                        value={v.url || ""}
                        onChange={(e) => updateVideo(idx, { url: e.target.value })}
                        placeholder="https://www.youtube.com/embed/..."
                        disabled={readonly}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Description (optional)</Label>
                  <Textarea
                    value={v.description || ""}
                    onChange={(e) => updateVideo(idx, { description: e.target.value })}
                    rows={2}
                    disabled={readonly}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <Switch
                      checked={!!v.required}
                      onCheckedChange={(val) => updateVideo(idx, { required: val })}
                      disabled={readonly}
                    />
                    Required
                  </label>
                  {!readonly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeVideo(idx)}
                      className="text-red-600 hover:bg-red-50 gap-1"
                    >
                      <Trash2 className="w-3 h-3" /> Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documents */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-slate-900">Documents</h3>
            <span className="text-xs text-slate-500">({kit.documents.length})</span>
          </div>
          {!readonly && (
            <label className="inline-flex">
              <input
                type="file"
                accept=".pdf,application/pdf,image/*"
                className="hidden"
                onChange={handleDocUpload}
                disabled={uploadingDoc}
                data-testid="upload-doc-input"
              />
              <Button variant="outline" size="sm" asChild className="gap-1 cursor-pointer">
                <span>
                  {uploadingDoc ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Upload className="w-3 h-3" />
                  )}
                  Upload Document
                </span>
              </Button>
            </label>
          )}
        </div>

        {kit.documents.length === 0 ? (
          <p className="text-sm text-slate-500 italic text-center py-6">No documents yet.</p>
        ) : (
          <div className="space-y-3">
            {kit.documents.map((d, idx) => (
              <div key={d.id || idx} className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50">
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <Label className="text-xs">Title</Label>
                    <Input
                      value={d.title || ""}
                      onChange={(e) => updateDoc(idx, { title: e.target.value })}
                      disabled={readonly}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Signature style</Label>
                    <Select
                      value={d.signature_style || "esign"}
                      onValueChange={(val) => updateDoc(idx, { signature_style: val })}
                      disabled={readonly}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="esign">Full e-signature (drawn)</SelectItem>
                        <SelectItem value="acknowledge">Acknowledgement checkbox only</SelectItem>
                        <SelectItem value="esign_or_ack">Either — new hire chooses</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Description (optional)</Label>
                  <Textarea
                    value={d.description || ""}
                    onChange={(e) => updateDoc(idx, { description: e.target.value })}
                    rows={2}
                    disabled={readonly}
                  />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <Switch
                        checked={!!d.required}
                        onCheckedChange={(val) => updateDoc(idx, { required: val })}
                        disabled={readonly}
                      />
                      Required
                    </label>
                    {(d.content_type || "").includes("pdf") && (
                      <div className="flex items-center gap-2" data-testid={`kit-doc-fillable-${idx}`}>
                        {d.fillable_form?.fields?.length > 0 ? (
                          <>
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                              <Switch
                                checked={!!d.fillable_form?.enabled}
                                onCheckedChange={(val) =>
                                  updateDoc(idx, { fillable_form: { ...d.fillable_form, enabled: val } })
                                }
                                disabled={readonly}
                                data-testid={`kit-doc-fillable-toggle-${idx}`}
                              />
                              Fillable
                            </label>
                            <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">
                              {d.fillable_form.fields.length} field{d.fillable_form.fields.length === 1 ? "" : "s"}
                              {d.fillable_form.auto_detected ? " · auto-detected" : ""}
                            </Badge>
                          </>
                        ) : (
                          <span className="text-xs text-slate-500">View &amp; sign only</span>
                        )}
                        {!readonly && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFieldEditorIdx(idx)}
                            className="gap-1 h-7 text-xs"
                            data-testid={`kit-doc-edit-fields-${idx}`}
                          >
                            {d.fillable_form?.fields?.length > 0 ? "Edit fields" : "Make fillable"}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  {!readonly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDoc(idx)}
                      className="text-red-600 hover:bg-red-50 gap-1"
                    >
                      <Trash2 className="w-3 h-3" /> Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Checklist */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-emerald-600" />
            <h3 className="font-semibold text-slate-900">Checklist</h3>
            <span className="text-xs text-slate-500">({kit.checklist.length})</span>
          </div>
          {!readonly && (
            <Button variant="outline" size="sm" onClick={addChecklistItem} className="gap-1" data-testid="add-checklist-btn">
              <Plus className="w-3 h-3" /> Add Item
            </Button>
          )}
        </div>

        {kit.checklist.length === 0 ? (
          <p className="text-sm text-slate-500 italic text-center py-6">No checklist items yet.</p>
        ) : (
          <div className="space-y-3">
            {kit.checklist.map((c, idx) => (
              <div key={c.id || idx} className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input
                    value={c.title || ""}
                    onChange={(e) => updateChecklistItem(idx, { title: e.target.value })}
                    disabled={readonly}
                  />
                </div>
                <div>
                  <Label className="text-xs">Description (optional)</Label>
                  <Textarea
                    value={c.description || ""}
                    onChange={(e) => updateChecklistItem(idx, { description: e.target.value })}
                    rows={2}
                    disabled={readonly}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <Switch
                      checked={!!c.required}
                      onCheckedChange={(val) => updateChecklistItem(idx, { required: val })}
                      disabled={readonly}
                    />
                    Required
                  </label>
                  {!readonly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeChecklistItem(idx)}
                      className="text-red-600 hover:bg-red-50 gap-1"
                    >
                      <Trash2 className="w-3 h-3" /> Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {fieldEditorIdx !== null && kit.documents[fieldEditorIdx] && (
        <FillableFormEditor
          open
          onClose={() => setFieldEditorIdx(null)}
          file={{
            id: kit.documents[fieldEditorIdx].id || `doc-${fieldEditorIdx}`,
            filename: kit.documents[fieldEditorIdx].title,
            fillable_form: kit.documents[fieldEditorIdx].fillable_form,
          }}
          endpoints={{
            pdf: `${API}/settings/orientation-kit/document-file?storage_path=${encodeURIComponent(kit.documents[fieldEditorIdx].storage_path)}`,
            info: `${API}/settings/orientation-kit/document-info?storage_path=${encodeURIComponent(kit.documents[fieldEditorIdx].storage_path)}`,
            detect: `${API}/settings/orientation-kit/document-detect`,
            detectBody: { storage_path: kit.documents[fieldEditorIdx].storage_path },
          }}
          onSaveFields={async (fields, info) => {
            updateDoc(fieldEditorIdx, {
              fillable_form: {
                enabled: true,
                auto_detected: false,
                page_count: info.page_count,
                page_sizes: info.page_sizes,
                fields,
              },
            });
            toast.info("Remember to click “Save Kit” to publish the changes");
          }}
          onClearFields={async () => updateDoc(fieldEditorIdx, { fillable_form: null })}
        />
      )}
    </div>
  );
}
